/* eslint-disable max-lines */

import { push } from 'connected-react-router';
import produce from 'immer';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { DatacenterApi, EnvironmentApi, ProductPolicyApi } from 'vrlcm-gui-api';
import {
  DatacenterV2,
  EnvironmentRequest,
  EnvironmentState,
  LockerAliasVmid,
  PolicyProduct,
  ProductProperties,
  RequestState,
  RequestType,
  SelectedProduct,
  UIData
} from 'vrlcm-gui-types';
import { toSafeSelectedProducts, toSafeUIData } from 'vrlcm-gui-utils';
import {
  GenerateOrganicGrowthRequest,
  generateOrganicGrowthRequestFailed,
  generateOrganicGrowthRequestSuccess,
  GenerateScaleOutRequest,
  generateScaleOutRequestFailed,
  generateScaleOutRequestSuccess
} from '../../actions/Environments';
import {
  GenerateEnvironmentRequest,
  generateEnvironmentRequestFailed,
  generateEnvironmentRequestSuccess,
  InitializeWorkflow,
  initializeWorkflowFailed,
  initializeWorkflowSuccess,
  PatchEnvironmentState,
  patchEnvironmentStateFailed,
  patchEnvironmentStateSuccess,
  SaveEnvironmentWorkflow,
  saveEnvironmentWorkflowFailed,
  saveEnvironmentWorkflowSuccess,
  SubmitEnvironmentWorkflow,
  submitEnvironmentWorkflowFailed,
  submitEnvironmentWorkflowSuccess
} from '../../actions/workflows/EnvironmentWorkflow';
import EnvironmentWorkflowAPI from '../../api/workflows/EnvironmentWorkflow';
import * as Constants from '../../constants/action-constants';
import { TOTAL_STEPS } from '../../constants/AppConstants';
import { BASE_ROUTE, CLOUD, ROUTES } from '../../constants/RouteConstants';
import { getWorkflowSkeleton } from '../../mocks/specs';
import { StoreState } from '../../types';
import { generateUniqueId, isVidm } from '../../utils/Helpers';
import binaryMappingSaga from './steps/BinaryMapping';
import environmentSaga from './steps/Environment';
import eulaSaga from './steps/Eula';
import infrastructureSaga from './steps/Infrastructure';
import networkSaga from './steps/Network';
import precheckSaga from './steps/Precheck';
import productsSaga from './steps/Products';
import selectProductSaga from './steps/SelectProduct';
import { resyncOrganicGrowthRequest, updateParentEnvironment } from './util';
import { requestPasswords } from '../../actions/common';

function* generateEnvironmentRequest(action: GenerateEnvironmentRequest) {
  try {
    const { isCloudProxyEnvironment } = action.payload;
    let products: PolicyProduct[] = yield select(
      (s: StoreState) => s.environmentWorkflow.steps.selectProduct.products
    );
    if (products.length <= 0 || isCloudProxyEnvironment) {
      products = yield call(ProductPolicyApi.getProxyPolicies);
    }
    if (products.length <= 0 || isVidm(products)) {
      products = yield call(ProductPolicyApi.getProductPolicies);
    }
    const requestId = generateUniqueId();
    const workflowSkeleton = getWorkflowSkeleton();
    //TODO: using isCloudProxyEnvironment in uiData
    const state = produce<EnvironmentState, EnvironmentState>(
      workflowSkeleton,
      draft => {
        const uiData: UIData = {
          currentStep: 0,
          totalSteps: 2,
          stepStates: ['warning', undefined],
          isCloudProxyEnvironment
        };
        draft.requestId = requestId;
        draft.uiData = JSON.stringify(uiData);
      }
    );
    yield put(generateEnvironmentRequestSuccess(state, products));
  } catch (e) {
    yield put(generateEnvironmentRequestFailed());
  }
}

function* watchForGenerateEnvironmentRequest() {
  yield takeLatest(
    Constants.GENERATE_ENVIRONMENT_REQUEST,
    generateEnvironmentRequest
  );
}

function* generateScaleOutRequest(action: GenerateScaleOutRequest) {
  try {
    const { updatedEnvironment, scaleOutState } = action.payload;
    yield call(
      EnvironmentWorkflowAPI.patchCreateEnvironmentDetails,
      updatedEnvironment
    );
    yield call(
      EnvironmentWorkflowAPI.patchScaleOutProductDetails,
      scaleOutState
    );
    yield put(generateScaleOutRequestSuccess(scaleOutState));
    yield put(
      push(`${ROUTES.ENVIRONMENT_WORKFLOW}/${scaleOutState.requestId}`)
    );
  } catch (e) {
    yield put(generateScaleOutRequestFailed((e as any)?.response?.status));
  }
}

function* watchForGenerateScaleOutRequest() {
  yield takeLatest(
    Constants.GENERATE_SCALE_OUT_REQUEST,
    generateScaleOutRequest
  );
}

function* generateOrganicGrowthRequest(action: GenerateOrganicGrowthRequest) {
  try {
    const { updatedEnvironment, organicGrowthState } = action.payload;
    yield call(
      EnvironmentWorkflowAPI.patchCreateEnvironmentDetails,
      updatedEnvironment
    );
    yield call(
      EnvironmentWorkflowAPI.patchOrganicGrowthDetails,
      organicGrowthState
    );
    yield put(generateOrganicGrowthRequestSuccess(organicGrowthState));
    yield put(
      push(`${ROUTES.ENVIRONMENT_WORKFLOW}/${organicGrowthState.requestId}`)
    );
  } catch (e) {
    yield put(generateOrganicGrowthRequestFailed());
  }
}

function* watchForGenerateOrganicGrowthRequest() {
  yield takeLatest(
    Constants.GENERATE_ORGANIC_GROWTH_REQUEST,
    generateOrganicGrowthRequest
  );
}

const getEnvironmentState = (state: StoreState) =>
  state.environmentWorkflow.state;

const getPasswordState = (state: StoreState) => state.common.passwords;

function* initializeWorkflow(action: InitializeWorkflow) {
  try {
    const { requestId } = action.payload;
    let state: EnvironmentState = yield select(getEnvironmentState);
    const datacenters: DatacenterV2[] = yield call(
      DatacenterApi.getDatacentersWithChildren
    );
    yield put(requestPasswords());
    yield take([
      Constants.REQUEST_PASSWORDS_SUCCESS,
      Constants.REQUEST_PASSWORDS_FAILED
    ]);
    const passwords: LockerAliasVmid[] = yield select(getPasswordState);

    if (requestId) {
      state = yield call(EnvironmentWorkflowAPI.getEnvironmentState, requestId);
      let selectedProducts: SelectedProduct[] = [];
      const datacenterVmids = datacenters.map(d => d.dataCenterVmid);
      const passwordVmids = passwords.map(
        p => `locker:password:${p.vmid}:${p.alias}`
      );
      const uiData: UIData = {
        currentStep: 0,
        totalSteps: 2,
        stepStates: ['warning', undefined]
      };
      if (!state.requestId) {
        // Redeploy Scenario
        const workflowSkeleton = getWorkflowSkeleton();
        workflowSkeleton.requestId = requestId;
        workflowSkeleton.uiData = JSON.stringify(uiData);
        try {
          const requestData: EnvironmentState = yield call(
            EnvironmentApi.getRedeployRequestData,
            requestId
          );
          if (requestData) {
            const { environmentRequestDTO } = requestData;
            uiData.isCloudProxyEnvironment =
              environmentRequestDTO?.metaData?.isCloudProxyEnvironment ===
              'true';
            workflowSkeleton.uiData = JSON.stringify(uiData);
          }
          state = requestData || workflowSkeleton;
        } catch (e) {
          state = workflowSkeleton;
        }
        if (!state.requestType) {
          state.requestType = RequestType.CREATE_ENVIRONMENT;
        }
        try {
          selectedProducts = toSafeSelectedProducts(
            state.environmentRequestDTO
          );
        } catch (error) {
          selectedProducts = state.environmentRequestDTO.products.map<
            SelectedProduct
          >(p => ({
            id: p.id,
            selectedVersion: p.version,
            type:
              p.properties.isImport && p.properties.isImport === 'true'
                ? 'import'
                : 'new',
            selected: true,
            selectedDeploymentType: '',
            sizes: {},
            description: '',
            detailsHref: '',
            errorMessage: '',
            productVersions: []
          }));
          state.environmentRequestDTO.infrastructure.properties._selectedProducts = JSON.stringify(
            selectedProducts
          );
        }
        const vidm = isVidm(state.environmentRequestDTO.products);
        const isImport = selectedProducts.every(p => p.type === 'import');
        let totalSteps = 0;
        switch (state.requestType) {
          case RequestType.VALIDATE_AND_CREATE_ENVIRONMENT:
          case RequestType.CREATE_ENVIRONMENT:
            totalSteps = vidm ? TOTAL_STEPS.vidm : TOTAL_STEPS.regular;
            totalSteps = isImport ? 3 : totalSteps - 1; // -1 to remove select product step
            break;
          case RequestType.ADD_NODE:
            totalSteps = 4;
            break;
          case RequestType.ADD_PRODUCT:
            totalSteps = isImport ? 3 : 7; // -1 to remove select product step
            break;
        }
        state.uiData = JSON.stringify(uiData);
        state.requestState = RequestState.UI_INPROGRESS;
        state.resultData = '';
        state.environmentRequestDTO.infrastructure.properties._isRedeploy =
          'true';
      } else {
        if (state.requestType === RequestType.ADD_NODE) {
          // Resync Scaleout Request
          // @TODO: Use Indiviual Environment and product API once its there
          const environment: EnvironmentRequest = yield call(
            EnvironmentApi.getEnvironmentRequestById,
            state.environmentRequestDTO.environmentId
          );
          const product = environment.products.find(
            p => p.id === state.environmentRequestDTO.products[0].id
          );
          const {
            'snapshot:takeSnapshot': takeSnapshot,
            'snapshot:isRetainSnapshot': isRetainSnapshot
          } = state.environmentRequestDTO.products[0].properties;
          if (product) {
            state.environmentRequestDTO.products[0].version = product.version;
            Object.assign(state.environmentRequestDTO.products[0].properties, {
              ...product.properties,
              'snapshot:takeSnapshot': takeSnapshot,
              'snapshot:isRetainSnapshot': isRetainSnapshot
            });
          }
        } else if (state.requestType === RequestType.ADD_PRODUCT) {
          // Resync Organic Growth Request
          yield resyncOrganicGrowthRequest(state);
        }
        const {
          dataCenterVmid,
          vCenterName,
          vCenterHost,
          defaultPassword
        } = state.environmentRequestDTO.infrastructure.properties;
        if (
          !datacenterVmids.includes(dataCenterVmid) ||
          !passwordVmids.includes(defaultPassword)
        ) {
          const uiData = toSafeUIData(state.uiData);
          Object.assign(uiData, { currentStep: 0 });
          uiData.stepStates[0] = 'warning';
          state.uiData = JSON.stringify(uiData);
        } else {
          const selectedDataCenter = datacenters.find(
            dc => dc.dataCenterVmid === dataCenterVmid
          );
          if (selectedDataCenter && vCenterName && vCenterHost) {
            const selectedVCenter = selectedDataCenter.vCenters?.find(
              vc =>
                vc.vCenterHost === vCenterHost && vc.vCenterName === vCenterName
            );
            if (!selectedVCenter) {
              const uiData = toSafeUIData(state.uiData);
              Object.assign(uiData, { currentStep: 0 });
              state.uiData = JSON.stringify(uiData);
              Object.assign(
                state.environmentRequestDTO.infrastructure.properties,
                {
                  vCenterName: '',
                  vCenterHost: ''
                }
              );
            }
          }
        }
      }
    }
    yield put(initializeWorkflowSuccess(datacenters, state));
  } catch (e) {
    yield put(initializeWorkflowFailed());
  }
}

function* watchForInitailizeWorkflow() {
  yield takeLatest(Constants.INITIALIZE_WORKFLOW, initializeWorkflow);
}

function* patchEnvironmentState(action: PatchEnvironmentState) {
  try {
    const { state } = action.payload;
    if (state.requestType === RequestType.ADD_NODE) {
      yield call(EnvironmentWorkflowAPI.patchScaleOutProductDetails, state);
    } else if (state.requestType === RequestType.ADD_PRODUCT) {
      yield call(EnvironmentWorkflowAPI.patchOrganicGrowthDetails, state);
    } else {
      yield call(EnvironmentWorkflowAPI.patchCreateEnvironmentDetails, state);
    }
    yield put(patchEnvironmentStateSuccess(state));
  } catch (e) {
    yield put(patchEnvironmentStateFailed());
  }
}

function* watchForPatchState() {
  yield takeLatest(Constants.PATCH_ENVIRONMENT_STATE, patchEnvironmentState);
}

function* saveEnvironmentWorkflow(action: SaveEnvironmentWorkflow) {
  try {
    const { state, exit } = action.payload;
    const isCloudProxyEnvironment = toSafeUIData(state.uiData)
      .isCloudProxyEnvironment;
    if (state.requestType === RequestType.ADD_NODE) {
      yield call(EnvironmentWorkflowAPI.patchScaleOutProductDetails, state);
    } else if (state.requestType === RequestType.ADD_PRODUCT) {
      yield call(EnvironmentWorkflowAPI.patchOrganicGrowthDetails, state);
    } else {
      yield call(EnvironmentWorkflowAPI.patchCreateEnvironmentDetails, state);
    }
    yield put(saveEnvironmentWorkflowSuccess(state));
    if (exit) {
      yield put(push(
        isCloudProxyEnvironment ? CLOUD : BASE_ROUTE
      ));
    }
  } catch (e) {
    yield put(saveEnvironmentWorkflowFailed());
  }
}

function* watchForSaveEnvironment() {
  yield takeLatest(
    Constants.SAVE_ENVIRONMENT_WORKFLOW,
    saveEnvironmentWorkflow
  );
}

function* submitEnvironmentWorkflow(action: SubmitEnvironmentWorkflow) {
  try {
    const { state } = action.payload;
    const requestId = state.requestId;
    if (state.requestType === RequestType.ADD_NODE) {
      const nextState = produce<EnvironmentState, EnvironmentState>(
        state,
        draft => {
          const properties = Object.entries(
            draft.environmentRequestDTO.products[0].properties
          ).reduce<ProductProperties>((acc, [propKey, value]) => {
            const includedKeys = ['contentLibraryItemId', 'snapshot'];
            if (includedKeys.some(key => propKey.includes(key))) {
              Object.assign(acc, { [propKey]: value });
            }
            return acc;
          }, {});
          draft.environmentRequestDTO.products[0].properties = properties;
          draft.environmentRequestDTO.products[0].nodes = state.environmentRequestDTO.products[0].nodes.filter(
            n => n.properties.disabled !== 'true'
          );
          if (
            draft.environmentRequestDTO.products[0].id === 'vrops' &&
            draft.environmentRequestDTO.products[0].collectorGroups &&
            draft.environmentRequestDTO.products[0].collectorGroups
              .collectorGroups
          ) {
            draft.environmentRequestDTO.products[0].collectorGroups.collectorGroups =
              state.environmentRequestDTO.products[0].collectorGroups?.collectorGroups.filter(
                cg => cg.description !== 'disabled'
              ) || [];

            if (
              draft.environmentRequestDTO.products[0].collectorGroups
                .collectorGroups.length === 0
            ) {
              delete draft.environmentRequestDTO.products[0].collectorGroups;
            }
          }
        }
      );
      yield call(EnvironmentWorkflowAPI.patchScaleOutProductDetails, nextState);
      const productId = state.environmentRequestDTO.products[0].id;
      yield updateParentEnvironment(state.uiData, 'scale-out', productId);
    } else if (state.requestType === RequestType.ADD_PRODUCT) {
      yield call(EnvironmentWorkflowAPI.patchOrganicGrowthDetails, state);
      yield updateParentEnvironment(state.uiData, 'organic-growth');
    } else {
      const nextState = produce<EnvironmentState, EnvironmentState>(
        state,
        draft => {
          draft.environmentRequestDTO.infrastructure.properties.__isInstallerRequest =
            'false';
        }
      );
      yield call(
        EnvironmentWorkflowAPI.patchCreateEnvironmentDetails,
        nextState
      );
    }

    yield put(submitEnvironmentWorkflowSuccess());
    yield put(push(`${ROUTES.REQUESTS}/${requestId}`));
  } catch (e) {
    yield put(submitEnvironmentWorkflowFailed());
  }
}

function* watchForSubmitEnvironment() {
  yield takeLatest(
    Constants.SUBMIT_ENVIRONMENT_WORKFLOW,
    submitEnvironmentWorkflow
  );
}

export default function* environmentWorkflowSaga() {
  yield all([
    environmentSaga(),
    selectProductSaga(),
    eulaSaga(),
    infrastructureSaga(),
    binaryMappingSaga(),
    networkSaga(),
    productsSaga(),
    precheckSaga(),
    watchForGenerateEnvironmentRequest(),
    watchForGenerateScaleOutRequest(),
    watchForGenerateOrganicGrowthRequest(),
    watchForInitailizeWorkflow(),
    watchForPatchState(),
    watchForSaveEnvironment(),
    watchForSubmitEnvironment()
  ]);
}
